// "Sign in with Apple" web flow.
//
// Three responsibilities split into three small functions so each is testable
// in isolation:
//   1. loadAppleSdk()      — inject Apple's <script> tag once, return a
//                            cached promise so concurrent / repeat callers
//                            don't re-inject.
//   2. initAppleSdk()      — call window.AppleID.auth.init() once with the
//                            brand-correct Services ID picked from hostname.
//   3. signInWithApple()   — open the Apple popup, exchange the resulting
//                            id_token at the backend, return {token, user}.
//
// The SDK script is loaded at runtime via DOM injection, NOT bundled by
// webpack — that keeps the marketing-page bundle clean and respects the
// 1 GB RAM deploy constraint.

import { StoredUser } from "../utils/authStorage";
import {
  APPLE_SCOPE,
  APPLE_SDK_URL,
  getAppleClientId,
  getAppleRedirectUri,
} from "../config/appleAuthConfig";
import { apiFetch } from "./apiClient";

let sdkPromise: Promise<void> | null = null;
let initialized = false;

// Test-only reset hook. Not exported through any barrel and never called
// from production code — keeps test isolation cheap without exposing
// mutable module state to consumers.
export const __resetAppleAuthForTests = (): void => {
  sdkPromise = null;
  initialized = false;
};

export const loadAppleSdk = (): Promise<void> => {
  if (sdkPromise) return sdkPromise;
  if (typeof document === "undefined") {
    return Promise.reject(new Error("Apple SDK can only load in a browser"));
  }

  sdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${APPLE_SDK_URL}"]`
    );
    if (existing && window.AppleID) {
      resolve();
      return;
    }

    const script = existing ?? document.createElement("script");
    script.src = APPLE_SDK_URL;
    script.async = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => {
      sdkPromise = null;
      reject(new Error("Failed to load Apple Sign-In SDK"));
    });
    if (!existing) document.head.appendChild(script);
  });

  return sdkPromise;
};

export const initAppleSdk = (): void => {
  if (initialized) return;
  if (!window.AppleID) {
    throw new Error("Apple SDK is not loaded");
  }
  const clientId = getAppleClientId();
  if (!clientId) {
    throw new Error(
      "Apple Sign-In is not configured for this hostname. " +
        "Set REACT_APP_APPLE_DEV_SERVICES_ID for local dev or register " +
        "the production hostname in src/config/appleAuthConfig.ts."
    );
  }
  window.AppleID.auth.init({
    clientId,
    scope: APPLE_SCOPE,
    redirectURI: getAppleRedirectUri(),
    usePopup: true,
  });
  initialized = true;
};

export interface AppleSignInResult {
  token: string;
  user: StoredUser;
}

interface AuthBackendResponse {
  access_token: string;
  name?: string | null;
  email?: string | null;
}

export const signInWithApple = async (): Promise<AppleSignInResult> => {
  if (!window.AppleID) {
    throw new Error("Apple SDK is not loaded");
  }

  const data = await window.AppleID.auth.signIn();
  const idToken = data?.authorization?.id_token;
  if (!idToken) {
    throw new Error("Apple did not return an id_token");
  }

  const response = await apiFetch<AuthBackendResponse>(
    "/v1/auth/signin/apple_web",
    {
      method: "POST",
      body: { identity_token: idToken },
    }
  );

  if (!response?.access_token) {
    throw new Error("Auth backend did not return an access_token");
  }

  return {
    token: response.access_token,
    user: {
      // Apple only returns `name` on the very first sign-in. The backend
      // is expected to echo the persisted name on subsequent calls; if
      // it doesn't (or sends null), default to "" so the UI can fall
      // back to a generic initial without crashing.
      name: response.name ?? "",
      email: response.email ?? "",
    },
  };
};
